import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]); // [{product, expires_at}]
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/cart", { withCredentials: true });
      setItems(r.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Periodically refresh so expired reservations are removed from view automatically.
  useEffect(() => {
    const t = setInterval(refresh, 30 * 1000);
    return () => clearInterval(t);
  }, [refresh]);

  const add = useCallback(async (productId) => {
    const r = await api.post("/cart/reserve", { product_id: productId }, { withCredentials: true });
    await refresh();
    setDrawerOpen(true);
    return r.data;
  }, [refresh]);

  const remove = useCallback(async (productId) => {
    await api.delete(`/cart/reserve/${productId}`, { withCredentials: true });
    await refresh();
  }, [refresh]);

  const open = useCallback(() => setDrawerOpen(true), []);
  const close = useCallback(() => setDrawerOpen(false), []);

  const count = items.length;
  const subtotal = useMemo(() => items.reduce((acc, it) => acc + Number(it.product?.price || 0), 0), [items]);

  return (
    <CartContext.Provider value={{ items, count, subtotal, loading, drawerOpen, open, close, add, remove, refresh }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
